import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import GenericPage from '../views/GenericPage.vue'
import NotFound from '../views/NotFound.vue'
import { bookingsRoutes } from './bookings'
import { menuRoutes } from './menus'
import { realTimeRoutes } from './realTime'
import { sessionsRoutes } from './sessions'
import { userRoutes } from './user'
import { travelPassRoutes } from './travelPasses'
import { amendRoutes } from './amends'
import { routeRoutes } from './routes'
import { redirections } from './redirections'
import { mobileRoutes } from './mobileRoutes'

declare module 'vue-router' {
  interface RouteMeta {
    /**
     * Opt in to search engine indexing. Every route without it is served with
     * `noindex, nofollow, noarchive` by `useRobots`, so private URLs such as
     * booking confirmations cannot leak into search results. Only add it to
     * pages that are safe for anyone to open, and list them in
     * `public/sitemap.xml`.
     */
    indexable?: boolean
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Home',
    component: () =>
      import(/* webpackChunkName: "home" */ '../views/Home.vue'),
    meta: {
      showHeader: true,
      showFooter: true,
      indexable: true
    }
  },
  ...sessionsRoutes,
  ...userRoutes,
  ...bookingsRoutes,
  ...realTimeRoutes,
  ...menuRoutes,
  ...travelPassRoutes,
  ...amendRoutes,
  ...routeRoutes,
  ...redirections,
  {
    path: '/cookie-policy',
    name: 'Cookie Policy',
    component: () =>
      import(/* webpackChunkName: "cookies" */ '../views/CookiePolicy.vue'),
    meta: {
      showHeader: true,
      showFooter: true,
      indexable: true
    }
  },
  {
    path: '/:slug',
    name: 'Content Page',
    component: GenericPage,
    props: true,
    meta: {
      showHeader: true,
      showFooter: true,
      indexable: true
    }
  },
  ...mobileRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'Not Found',
    component: NotFound,
    meta: {
      showHeader: true,
      showFooter: true
    }
  }
]

if (process.env.NODE_ENV === 'development') {
  routes.push({
    path: '/vectors-preview',
    name: 'Vectors',
    component: () =>
      import('../views/VectorList.vue'),
    meta: {
      showHeader: true,
      showFooter: true
    }
  })
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  scrollBehavior: (to, _from, savedPosition) => {
    if (to.hash) return { el: to.hash }
    else if (savedPosition) return savedPosition
    return { left: 0, top: 0 }
  },
  routes
})

// Prerender.io renders one URL per snapshot, and an auth guard can bounce that
// navigation before the target component ever renders (e.g. /my-account -> Home
// for the always-unauthenticated crawler). Resolving the entry URL up front
// keeps the noindex tag on the HTML served for that URL.
export const entryRouteIsNoindex =
  router.resolve(window.location.pathname).meta.noindex === true

router.afterEach(() => {
  document.getElementById('main-logo')?.focus()
  return true
})

export default router
